import React, { useState, useRef } from 'react';
import { Button } from '@mui/material';
import {
  ComparisonSection,
  DocumentSelectionArea,
  FileInputContainer,
  FileNameDisplay,
  ButtonGroup,
  ExtractionResults,
  StyledButton,
  SummaryStats,
  StatCard,
  StatValue,
  StatLabel,
} from './DocExtractContainer.styles';
import { Compare, UploadFile } from '@mui/icons-material';
import { documentExtractionApi } from '@api/api';
import ReactJson from 'react-json-view';

// Utility functions for metadata
function getDeepestLevel(obj, current = 1) {
  if (obj && typeof obj === 'object') {
    if (Array.isArray(obj)) {
      if (obj.length === 0) return current;
      return Math.max(...obj.map(item => getDeepestLevel(item, current + 1)));
    } else {
      const values = Object.values(obj);
      if (values.length === 0) return current;
      return Math.max(...values.map(value => getDeepestLevel(value, current + 1)));
    }
  }
  return current;
}

function getKeyValueCount(obj) {
  if (obj && typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.reduce((acc, item) => acc + getKeyValueCount(item), 0);
    } else {
      return Object.values(obj).reduce((acc, value) => acc + getKeyValueCount(value), Object.keys(obj).length);
    }
  }
  return 0;
}

function getSizeInBytes(obj) {
  try {
    return new TextEncoder().encode(JSON.stringify(obj)).length;
  } catch {
    return 0;
  }
}

function getComplexityIndex(depth, keyCount) {
  // Simple example: product of depth and key count
  return depth * keyCount;
}

export default function DocumentComparisonContainer() {
  const [file1, setFile1] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionMade, setExtractionMade] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile1Change = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile1(file);
      setExtractionMade(false);
      setExtractionResult(null);
      setError(null);
    }
  };

  const handleCompare = async () => {
    if (!file1) {
      setError("Please select a document to extract.");
      return;
    }
    setError(null);
    setIsLoading(true);
    setExtractionMade(false);
    setExtractionResult(null);

    try {
      const result = await documentExtractionApi.extractDocument(file1);
      setExtractionResult(result);
      setExtractionMade(true);
    } catch (err) {
      console.error("Extraction API failed:", err);
      setError(err.message || "Failed to extract document. Please try again.");
      setExtractionResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const FileInput = ({ id, label, selectedFile, onChange }) => (
    <FileInputContainer>
      <h3>{label}</h3>
      <p>Select the document file</p>
      <Button
        component="label"
        variant="outlined"
        startIcon={<UploadFile />}
        fullWidth
        style={{ marginBottom: '10px' }}
      >
        Upload File
        <input
          id={id}
          type="file"
          hidden
          accept=".doc,.docx,.pdf,.md,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/markdown,text/plain"
          onChange={onChange}
        />
      </Button>
      {selectedFile && (
        <FileNameDisplay>Selected: {selectedFile.name}</FileNameDisplay>
      )}
    </FileInputContainer>
  );

  return (
    <ComparisonSection>
      <DocumentSelectionArea>
        <FileInput
          id="file-input-1"
          label="Document"
          selectedFile={file1}
          onChange={handleFile1Change}
        />
      </DocumentSelectionArea>

      {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

      <ButtonGroup>
        <StyledButton
          variant="contained"
          startIcon={<Compare />}
          onClick={handleCompare}
          disabled={!file1 || isLoading}
        >
          {isLoading ? 'Extracting...' : 'Extract Document'}
        </StyledButton>
      </ButtonGroup>

      {extractionMade && extractionResult && !isLoading && (
        <ExtractionResults>
          {extractionResult["payload"] && (() => {
            let obj;
            try {
              obj = typeof extractionResult["payload"] === "string"
                ? JSON.parse(extractionResult["payload"])
                : extractionResult["payload"];
            } catch (e) {
              // Fallback: show as plain text if not valid JSON
              return (
                <pre style={{ background: "#f6f8fa", padding: "1em", borderRadius: "5px", overflowX: "auto" }}>
                  {extractionResult["payload"]}
                </pre>
              );
            }

            // --- Metadata calculations ---
            const depth = getDeepestLevel(obj);
            const keyCount = getKeyValueCount(obj);
            const sizeBytes = getSizeInBytes(obj);
            const sizeKB = (sizeBytes / 1024).toFixed(2);
            const complexity = getComplexityIndex(depth, keyCount);

            return (
              <>
                <div style={{ fontWeight: 600, fontSize: "1.15em", marginBottom: "0.5em" }}>
                  Extracted Information:
                </div>
                <SummaryStats>
                  <StatCard>
                    <StatValue>{depth}</StatValue>
                    <StatLabel>Deepest nested level</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{sizeBytes} bytes<br /><span style={{ fontSize: "1rem", color: "#888" }}>({sizeKB} KB)</span></StatValue>
                    <StatLabel>Payload size</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{keyCount}</StatValue>
                    <StatLabel>Total key/value pairs</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{complexity}</StatValue>
                    <StatLabel>Complexity index</StatLabel>
                  </StatCard>
                </SummaryStats>
                <ReactJson
                  src={obj}
                  name={null}
                  collapsed={2}
                  enableClipboard={true}
                  displayDataTypes={true}
                  displayObjectSize={true}
                  iconStyle="circle"
                  indentWidth={4}
                  theme={"tomorrow"}
                  style={{ padding: "1em", borderRadius: "5px" }}
                />
              </>
            );
          })()}
        </ExtractionResults>
      )}
      {isLoading && <p style={{ textAlign: 'center' }}>Loading comparison...</p>}
    </ComparisonSection>
  )
}
